import React, { FC, useState, useRef, useCallback } from 'react'
import { Box } from '@mui/material'
import { AnnotationBox, AnnotationFieldType } from './NumberAnnotationPanel'

interface AnnotationOverlayProps {
  selectedField: string
  annotations: AnnotationBox[]
  onAnnotationAdd: (annotation: AnnotationBox) => void
  fieldColors: Record<string, string>
  isActive: boolean
  imageContainerStyle?: React.CSSProperties
}

export const AnnotationOverlay: FC<AnnotationOverlayProps> = ({
  selectedField,
  annotations,
  onAnnotationAdd,
  fieldColors,
  isActive,
  imageContainerStyle = {},
}) => {
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 })
  const [currentBox, setCurrentBox] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)

  const overlayRef = useRef<HTMLDivElement>(null)

  // マウスイベントから相対座標を取得
  const getRelativeCoordinates = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!overlayRef.current) return { x: 0, y: 0 }

      const rect = overlayRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      return { x, y }
    },
    []
  )

  // ドラッグ開始
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isActive) return

      const coords = getRelativeCoordinates(e)
      setIsDrawing(true)
      setStartPoint(coords)
      setCurrentBox({ x: coords.x, y: coords.y, width: 0, height: 0 })
    },
    [getRelativeCoordinates, isActive]
  )

  // ドラッグ中
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDrawing || !isActive) return

      const coords = getRelativeCoordinates(e)
      const width = coords.x - startPoint.x
      const height = coords.y - startPoint.y

      setCurrentBox({
        x: width < 0 ? coords.x : startPoint.x,
        y: height < 0 ? coords.y : startPoint.y,
        width: Math.abs(width),
        height: Math.abs(height),
      })
    },
    [isDrawing, startPoint, getRelativeCoordinates, isActive]
  )

  // ドラッグ終了
  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentBox || !overlayRef.current || !isActive) {
      setIsDrawing(false)
      setCurrentBox(null)
      return
    }

    const containerWidth = overlayRef.current.offsetWidth
    const containerHeight = overlayRef.current.offsetHeight

    // 最小サイズのチェック
    if (currentBox.width < 10 || currentBox.height < 10) {
      setIsDrawing(false)
      setCurrentBox(null)
      return
    }

    const newAnnotation: AnnotationBox = {
      field: selectedField as AnnotationFieldType,
      origin_point_height_ratio: currentBox.y / containerHeight,
      origin_point_width_ratio: currentBox.x / containerWidth,
      range_height_ratio: currentBox.height / containerHeight,
      range_width_ratio: currentBox.width / containerWidth,
    }

    onAnnotationAdd(newAnnotation)
    setIsDrawing(false)
    setCurrentBox(null)
  }, [isDrawing, currentBox, selectedField, onAnnotationAdd, isActive])

  return (
    <Box
      ref={overlayRef}
      style={{
        ...imageContainerStyle,
        cursor: isActive ? 'crosshair' : 'default',
        pointerEvents: isActive ? 'auto' : 'none',
        zIndex: isActive ? 10 : 1,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* 既存のアノテーションボックス表示 */}
      {annotations.map((annotation, index) => (
        <Box
          key={`${annotation.field}-${index}`}
          sx={{
            position: 'absolute',
            left: `${annotation.origin_point_width_ratio * 100}%`,
            top: `${annotation.origin_point_height_ratio * 100}%`,
            width: `${annotation.range_width_ratio * 100}%`,
            height: `${annotation.range_height_ratio * 100}%`,
            bgcolor: fieldColors[annotation.field],
            border: `2px solid ${fieldColors[annotation.field].replace(
              '0.3',
              '1'
            )}`,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              left: 0,
              bgcolor: fieldColors[annotation.field].replace('0.3', '0.8'),
              color: '#000',
              px: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
            }}
          >
            {annotation.field}
          </Box>
        </Box>
      ))}

      {/* 現在描画中のボックス */}
      {currentBox && isActive && (
        <Box
          sx={{
            position: 'absolute',
            left: currentBox.x,
            top: currentBox.y,
            width: currentBox.width,
            height: currentBox.height,
            bgcolor: fieldColors[selectedField],
            border: `2px dashed ${fieldColors[selectedField].replace(
              '0.3',
              '1'
            )}`,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  )
}
